import type { TenantMembership } from './types';

const STORAGE_KEY = 'zebra-stock.auth.v1';

export interface PersistedAuthState {
  token: string | null;
  tenants: TenantMembership[];
  activeTenantId: string | null;
}

const EMPTY_STATE: PersistedAuthState = { token: null, tenants: [], activeTenantId: null };

function isTenantMembership(value: unknown): value is TenantMembership {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return typeof v.tenantId === 'string' && typeof v.label === 'string' && typeof v.role === 'string';
}

/** `settings` was added to `TenantMembership` on 2026-08-26 (per-tenant silo
 * gate) — a membership persisted to localStorage before that ships without it.
 * `isTenantMembership` above only checks the three original fields, so an
 * old entry passes the guard structurally missing `settings`; default it to
 * `{}` here rather than crashing the first time a feature gate reads
 * `membership.settings.silosEnabled` (absent = off is the correct read
 * either way). */
function normalizeSettings(m: TenantMembership): TenantMembership {
  return { ...m, settings: m.settings && typeof m.settings === 'object' ? m.settings : {} };
}

/** Best-effort read; any corruption (manual edits, an old schema version) falls
 * back to a clean empty state rather than throwing during app boot. */
export function loadAuthState(): PersistedAuthState {
  if (typeof window === 'undefined') return EMPTY_STATE;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY_STATE;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return EMPTY_STATE;
    const p = parsed as Record<string, unknown>;
    const tenants = Array.isArray(p.tenants) ? p.tenants.filter(isTenantMembership).map(normalizeSettings) : [];
    return {
      token: typeof p.token === 'string' ? p.token : null,
      tenants,
      activeTenantId: typeof p.activeTenantId === 'string' ? p.activeTenantId : null,
    };
  } catch {
    return EMPTY_STATE;
  }
}

export function saveAuthState(state: PersistedAuthState): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

export function clearAuthState(): void {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(STORAGE_KEY);
}
